/* eslint @typescript-eslint/no-explicit-any: 1 -- TODO fix types */
import { inject, Injectable } from '@angular/core';
import { Centrifuge, ServerPublicationContext } from 'centrifuge';
import { firstValueFrom, Observable } from 'rxjs';

import { UserService } from '@hosty-app/services';
import { ISocketMessage } from '@hosty-app/types';

import { environment } from '../../environments/environment';

export interface CentrifugeBase {
  listen(token: string, userID: number, legacy?: boolean): Observable<ISocketMessage<any>>;
}

@Injectable({ providedIn: 'root' })
export class CentrifugeNextService implements CentrifugeBase {
  #offset: number | null = null;
  #userService = inject(UserService);

  listen(token: string, userId: number, legacy = true): Observable<ISocketMessage<any>> {
    return new Observable((o) => {
      const instance = new Centrifuge(environment.baseWsUrl, {
        debug: /*isDevMode()*/ false,
        token,
        // After a long backgrounding the initial token has expired, so a
        // reconnect would fail auth and stop for good. This lets the SDK fetch a
        // fresh one as part of its normal reconnect cycle.
        getToken: () =>
          firstValueFrom(this.#userService.getCentrifugeToken(legacy)).then((r) => r.token),
      });
      instance.connect();
      instance.on('connected', () => {
        // Replays anything published while the socket was down — which on iOS is
        // every time the app was backgrounded.
        instance
          .history(
            `user#${userId}`,
            this.#offset === null
              ? {
                  limit: 0,
                  reverse: true,
                }
              : {
                  limit: 100,
                  since: { offset: this.#offset, epoch: '' },
                },
          )
          .then((res) => {
            res.publications.forEach((p) => {
              o.next(p.data);
            });
            this.#offset = res.offset;
          })
          .catch((err) => {
            // Losing the catch-up window is survivable; losing the socket is not.
            console.error('centrifuge history failed', err);
          });
      });
      instance.on('publication', (ctx: ServerPublicationContext) => {
        o.next(ctx.data);
        this.#offset = ctx.offset ?? null;
      });

      // 'error' and 'disconnected' are NOT terminal. Centrifuge v5 reconnects on
      // its own with backoff, and iOS drops this socket every time the app is
      // suspended. Completing or erroring the stream here tore down the only
      // subscription and killed real-time updates for the rest of the session —
      // the user kept the app open and silently stopped receiving anything.
      instance.on('error', (err) => {
        console.warn('centrifuge error', err);
      });
      instance.on('disconnected', (ctx) => {
        console.warn('centrifuge disconnected', ctx);
      });

      return () => {
        instance.disconnect();
      };
    });
  }
}
